import { Align, Button, Icon, Row, Theme } from '@busybusy/webapp-react-ui';
import { CheckDailyReports, CloseIcon } from 'assets/icons';
import classNames from 'classnames';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import DailyProjectReportEnableForm from 'containers/daily-project-report/DailyProjectReportEnableForm/DailyProjectReportEnableForm';
import DocumentEnableForm from 'containers/documents/DocumentEnableForm/DocumentEnableForm';
import ProgressTrackingEnableForm from 'containers/progress-report/ProgressTrackingEnableForm/ProgressTrackingEnableForm';
import { useActiveMember, useOpennable } from 'hooks';
import useMemberUiSettingsUpdate from 'hooks/models/member-settings/useMemberUiSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { useOrganizationSettings } from 'hooks/models/organization/settings/useOrganizationSettings/useOrganizationSettings';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useFeatureFlags } from 'utils/features';
import LocalStore from 'utils/localStorageUtils';
import { t } from 'utils/localize';
import { EnablePremiumDialog } from '../EnablePremiumDialog/EnablePremiumDialog';
import './DashboardAnnouncement.scss';

export interface IDashboardAnnouncementProps {
  className?: string | object;
  theme: Theme.DARK | Theme.LIGHT;
}

export const latestAnnouncementVersion = '2023.2';
export const latestAnnouncementKey = `announcement-${latestAnnouncementVersion}-content`;

const announcementLinksLookup = {
  allReleases: 'https://fast.wistia.com/embed/channel/fwd5oxatzi',
  release2022Dot10: 'https://fast.wistia.net/embed/channel/fwd5oxatzi?wchannelid=fwd5oxatzi&wmediaid=smg5bapmq7',
  release2023Dot1: 'https://fast.wistia.net/embed/channel/fwd5oxatzi?wchannelid=fwd5oxatzi&wmediaid=hwriddjbot',
  release2023Dot2: 'https://busybusy.wistia.com/medias/0vx391szrs',
  releaseOverview: 'https://busybusy.wistia.com/medias/nlk0xt9sf7',
  release2023Dot2Part1: 'https://busybusy.wistia.com/medias/w96t5camye',
  release2023Dot2Part2: 'https://busybusy.wistia.com/medias/634pqm1bek',
  release2023Dot2Part3: 'https://busybusy.wistia.com/medias/rpf4jxivvi',
  release2023Dot2Part4: 'https://busybusy.wistia.com/medias/pvoqm3vyl7',
  release2023Dot2Part5: 'https://busybusy.wistia.com/medias/208aoiqnrq',
};

const DashboardAnnouncement = (props: IDashboardAnnouncementProps) => {
  const { className, theme } = props;
  const classes = classNames('dashboard-annoucement', theme, 'mt-6', 'px-8', 'pt-4', 'pb-4', className);
  const memberSettings = useMemberSettings();
  const organizationSettings = useOrganizationSettings();

  const member = useActiveMember();
  const isPro = useFeatureFlags('PRO');
  const hasDocumentsAccess = useFeatureFlags('DOCUMENTS');
  const hasDailyProjectReportAccess = useFeatureFlags('DAILY_REPORTS');
  const hasProgressTrackingAccess = useFeatureFlags('WORK_ACCOMPLISHED');
  const [showDocumentsAction, setShowDocumentsAction] = useState(false);
  const [showDailyProjectReportAction, setShowDailyProjectReportAction] = useState(false);
  const [showProgressTrackingAction, setShowProgressTrackingAction] = useState(false);
  const updateUiSettings = useMemberUiSettingsUpdate();
  const memberSettingsAnnouncementVersion = memberSettings?.web?.ui?.hideAnnouncementTextForVersion;
  const showAnnouncement = memberSettingsAnnouncementVersion !== latestAnnouncementVersion;
  const canManageCompanySettings = member.position?.manageCompanySettings ?? false;

  const documentDialogOpennable = useOpennable();
  const documentRequestCompleteOpennable = useOpennable();
  const dailyProjectReportDialogOpennable = useOpennable();
  const dailyProjectReportRequestCompleteOpennable = useOpennable();
  const progressTrackingDialogOpennable = useOpennable();
  const progressTrackingRequestCompleteOpennable = useOpennable();
  const premiumDialogOpennable = useOpennable();

  const onClose = () => {
    updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
  };

  useOnMount(() => {
    if (
      latestAnnouncementVersion !== memberSettingsAnnouncementVersion &&
      !isNil(LocalStore.get(latestAnnouncementKey)) &&
      LocalStore.get(latestAnnouncementKey) === true
    ) {
      updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
    }
  });

  useEffect(() => {
    setShowDocumentsAction(!hasDocumentsAccess && organizationSettings?.web?.ui?.requestedDocumentsAccess !== true);
  }, [hasDocumentsAccess, organizationSettings]);
  useEffect(() => {
    setShowDailyProjectReportAction(
      !hasDailyProjectReportAccess && organizationSettings?.web?.ui?.requestedDailyProjectReportsAccess !== true
    );
  }, [hasDailyProjectReportAccess, organizationSettings]);
  useEffect(() => {
    setShowProgressTrackingAction(
      !hasProgressTrackingAccess && organizationSettings?.web?.ui?.requestedProgressTrackingAccess !== true
    );
  }, [hasProgressTrackingAccess, organizationSettings]);

  const onDocumentRequestSubmitted = () => {
    setShowDocumentsAction(false);
    documentRequestCompleteOpennable.open();
    documentDialogOpennable.close();
  };
  const onDailyProjectReportRequestSubmitted = () => {
    setShowDailyProjectReportAction(false);
    dailyProjectReportRequestCompleteOpennable.open();
    dailyProjectReportDialogOpennable.close();
  };
  const onProgressTrackingRequestSubmitted = () => {
    setShowProgressTrackingAction(false);
    progressTrackingRequestCompleteOpennable.open();
    progressTrackingDialogOpennable.close();
  };

  const onLinkClicked = (type: keyof typeof announcementLinksLookup) => {
    const link = announcementLinksLookup[type]!;
    window.open(link);
  };

  const renderTitle = () => (
    <Row align={Align.CENTER}>
      <p className="title">{t('Announcing Daily Reports with Checklists')}</p>
      <p className="title ml-3">|</p>
      <p className="title mrl-3">2023.2 Release</p>
    </Row>
  );

  const renderIcon = () => <Icon className="announcement-icon" svg={CheckDailyReports} />;

  const CurrentReleaseDetails = () => (
    <div className="content-column pb-5">
      {t('Generate and share detailed reports from the field - Including reports with Checklists and Tasks!')}
      <br />
      <span className="content">
        <a className="link mr-1" onClick={() => onLinkClicked('release2023Dot2Part1')}>
          {t('Watch this short Video (1:04)')}
        </a>
        {t(' to learn more about Daily Reports.')}
      </span>

      <br />

      <ConditionalContainer condition={isPro && showDailyProjectReportAction && canManageCompanySettings}>
        <Button className="mt-6" type="primary" onClick={dailyProjectReportDialogOpennable.open}>
          {t('Enable Daily Project Reports Feature')}
        </Button>
      </ConditionalContainer>

      <div className="content-title mt-6">{t('More 2023.2 Release Features')}</div>
      <ul className="p-0 ml-4 mb-8">
        <li>
          <span className="content">
            {t('3rd Level Activity Reports')}
            <a className="link ml-1" onClick={() => onLinkClicked('release2023Dot2Part2')}>
              {'(2:03)'}
            </a>
          </span>
        </li>
        <li>
          <span className="content">
            {t('Print Photos')}
            <a className="link ml-1" onClick={() => onLinkClicked('release2023Dot2Part3')}>
              {'(0:34)'}
            </a>
          </span>
        </li>
        <li>
          <span className="content">
            {t('Documents: Save As')}
            <a className="link ml-1" onClick={() => onLinkClicked('release2023Dot2Part4')}>
              {'(1:08)'}
            </a>
          </span>
        </li>
        <li>
          <span className="content">
            {t('CompanyCam 2.0')}
            <a className="link ml-1" onClick={() => onLinkClicked('release2023Dot2Part5')}>
              {'(1:11)'}
            </a>
          </span>
        </li>
      </ul>

      <a className="content-title-link" onClick={() => onLinkClicked('releaseOverview')}>
        {'Overview Video (1:18)'}
      </a>

      <br />

      <a className="content-title-link" onClick={() => onLinkClicked('release2023Dot2')}>
        {'Full Release Video (8:47)'}
      </a>
    </div>
  );

  const PreviousReleaseDetails = () => (
    <div className="ml-11">
      <ConditionalContainer
        condition={
          isPro &&
          canManageCompanySettings &&
          (showDocumentsAction || showDailyProjectReportAction || showProgressTrackingAction)
        }
      >
        <div className="content-title mt-8">{t('Available Upgrades')}</div>

        <ul>
          {isPro &&
            (showDocumentsAction || showDailyProjectReportAction || showProgressTrackingAction) &&
            canManageCompanySettings && (
              <li>
                <a className="link ml-1" onClick={premiumDialogOpennable.open}>
                  {'Premium'}
                </a>
              </li>
            )}
          {isPro && showDocumentsAction && canManageCompanySettings && (
            <li>
              <a className="link ml-1" onClick={documentDialogOpennable.open}>
                {'Documents'}
              </a>
            </li>
          )}
          {isPro && showDailyProjectReportAction && canManageCompanySettings && (
            <li>
              <a className="link ml-1" onClick={dailyProjectReportDialogOpennable.open}>
                {'Daily Project Reports'}
              </a>
            </li>
          )}
          {isPro && showProgressTrackingAction && canManageCompanySettings && (
            <li>
              <a className="link ml-1" onClick={progressTrackingDialogOpennable.open}>
                {'Progress Tracking'}
              </a>
            </li>
          )}
        </ul>
      </ConditionalContainer>

      <div className="content-title">{t('Recent Releases')}</div>

      <ul>
        <li>
          <a className="link ml-1" onClick={() => onLinkClicked('release2023Dot1')}>
            {'2023.1'}
          </a>
        </li>
        <li>
          <a className="link ml-1" onClick={() => onLinkClicked('release2022Dot10')}>
            {'2022.10'}
          </a>
        </li>
        <li>
          <a className="link ml-1" onClick={() => onLinkClicked('allReleases')}>
            {'View All'}
          </a>
        </li>
      </ul>
    </div>
  );

  const renderContent = () => {
    return (
      <Row className="mt-2">
        <CurrentReleaseDetails />
        <PreviousReleaseDetails />
      </Row>
    );
  };

  const renderAnnouncement = () => {
    return (
      <>
        <div className={classes}>
          <IconButton className="close-icon" svg={CloseIcon} onClick={onClose} />
          <Row align={Align.CENTER} className={theme}>
            {renderIcon()}
            {renderTitle()}
          </Row>
          {renderContent()}
        </div>

        <HeaderDialog
          isOpen={documentDialogOpennable.isOpen}
          onClose={documentDialogOpennable.close}
          divider={false}
          title={t('Documents')}
        >
          <DocumentEnableForm className="pl-8 pr-8 pb-9" onRequestSubmitted={onDocumentRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Documents will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={documentRequestCompleteOpennable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={documentRequestCompleteOpennable.close}
        />

        <HeaderDialog
          isOpen={dailyProjectReportDialogOpennable.isOpen}
          onClose={dailyProjectReportDialogOpennable.close}
          divider={false}
          title={t('Daily Project Reports')}
        >
          <DailyProjectReportEnableForm
            className="pl-8 pr-8 pb-9"
            onRequestSubmitted={onDailyProjectReportRequestSubmitted}
          />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Daily Project Reports will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={dailyProjectReportRequestCompleteOpennable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={dailyProjectReportRequestCompleteOpennable.close}
        />

        <HeaderDialog
          isOpen={progressTrackingDialogOpennable.isOpen}
          onClose={progressTrackingDialogOpennable.close}
          divider={false}
          title={t('Progress Tracking')}
        >
          <ProgressTrackingEnableForm
            className="pl-8 pr-8 pb-9"
            onRequestSubmitted={onProgressTrackingRequestSubmitted}
          />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Progress Tracking will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={progressTrackingRequestCompleteOpennable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={progressTrackingRequestCompleteOpennable.close}
        />

        <HeaderDialog
          isOpen={premiumDialogOpennable.isOpen}
          onClose={premiumDialogOpennable.close}
          title={t('Additional Services')}
          size={'full'}
        >
          <EnablePremiumDialog />
        </HeaderDialog>
      </>
    );
  };

  return showAnnouncement ? renderAnnouncement() : <></>;
};

export default DashboardAnnouncement;
